import { Stars } from "@react-three/drei";
import { Canvas, useFrame } from "@react-three/fiber";
import React, { useRef } from "react";
import { FiArrowRight } from "react-icons/fi";
import { Link } from "react-router-dom"; 
// Import icons for moon phases
import { FiCircle, FiMoon } from "react-icons/fi";
import { BsCircleHalf, BsMoonStarsFill } from "react-icons/bs";
import "./MoonPhases.css";

// Animated stars component
const AnimatedStars = () => {
  const starsRef = useRef();
  
  useFrame(({ clock }) => {
    if (starsRef.current) {
      // Slow rotation on multiple axes for a cosmic feel
      starsRef.current.rotation.x = Math.sin(clock.getElapsedTime() * 0.05) * 0.2;
      starsRef.current.rotation.y = Math.sin(clock.getElapsedTime() * 0.1) * 0.2;
      starsRef.current.rotation.z += 0.0005; // Constant slow spin
    }
  });
  
  return (
    <Stars 
      ref={starsRef}
      radius={75} 
      depth={50} 
      count={3000} 
      factor={5} 
      saturation={1}
      fade 
      speed={1.5}
    />
  );
};

export const Heroabout = () => {

  return (
    <section
      className="relative grid min-h-105 sm:min-h-125 md:min-h-screen place-content-center overflow-hidden px-6 sm:px-8 py-16 sm:py-24 text-gray-200"
      style={{ backgroundColor: "#05091B" }}
    >
      {/* Moon Phases with improved positioning for different screen sizes */}
      {/* New Moon (top) - moved higher on small screens */}
      <div className="absolute top-2 sm:top-4 md:top-8 left-1/2 -translate-x-1/2 z-30 moon-phase moon-pulse">
        <div className="moon-orbit">
          <FiCircle className="text-indigo-200 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
        </div>
      </div>
      
      {/* Waxing Crescent (right) - moved further right on small screens */}
      <div className="absolute right-2 sm:right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 moon-phase moon-pulse moon-pulse-delay-1">
        <div className="moon-orbit">
          <BsCircleHalf className="text-indigo-200 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rotate-90 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
        </div>
      </div>
      
      {/* Full Moon (bottom) - moved lower on small screens */}
      <div className="absolute bottom-2 sm:bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 z-30 moon-phase moon-pulse moon-pulse-delay-2">
        <div className="moon-orbit">
          <BsMoonStarsFill className="text-indigo-200 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
        </div>
      </div>
      
      {/* Waning Crescent (left) - moved further left on small screens */}
      <div className="absolute left-2 sm:left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 moon-phase moon-pulse moon-pulse-delay-3">
        <div className="moon-orbit">
          <BsCircleHalf className="text-indigo-200 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 -rotate-90 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />
        </div>
      </div>

      <div className="relative z-20 flex flex-col items-center max-w-lg sm:max-w-lg md:max-w-3xl lg:max-w-7xl mx-auto w-full">
        <span className="mb-3 sm:mb-4 inline-block rounded-full bg-indigo-600/50 px-3 py-1.5 text-s sm:text-lg backdrop-blur-sm">
          Live Fully Aligned!
        </span>

        <div className="relative bg-[#0E1833] rounded-xl shadow-md p-4 sm:p-8 md:p-12 border border-indigo-700 text-left text-indigo-100 w-full max-w-xl sm:max-w-2xl md:max-w-4xl lg:max-w-5xl mx-auto">
          <h1
            className="text-center text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-medium leading-tight mb-6 sm:mb-8 text-indigo-200 max-w-full"
          >
            Andlegur: Your Space to Reflect, Renew, and Grow
          </h1>

          <p className="text-center text-base sm:text-lg lg:text-lg leading-relaxed lg:leading-relaxed mb-8 sm:mb-10 text-indigo-100 max-w-4xl mx-auto">
           Andlegur helps you understand your emotions, recognize patterns, and grow through guided reflection and intelligent insights.</p>

          <div className="flex justify-center mt-2">
            <Link
              to="/sanctuary"
              className="group relative flex w-fit items-center gap-1.5 rounded-full bg-indigo-600 hover:bg-indigo-800 text-white px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base transition-all duration-300 border border-indigo-500/50 backdrop-blur-md shadow-lg cursor-pointer"
            >
              Enter the Space
              <FiArrowRight className="transition-transform group-hover:translate-x-1 ml-1" />
              {/* Glow ring accent */}
              <span className="absolute -inset-1 rounded-full border-2 border-indigo-500 opacity-20 pointer-events-none animate-pulse" />
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute inset-0 z-0">
        <Canvas camera={{ position: [0, 0, 15], fov: 60 }}>
          <AnimatedStars />
          <ambientLight intensity={0.2} />
        </Canvas>
      </div>

      {/* Fused background image — web 30, sits above stars canvas */}
      <div className="absolute inset-0 z-1 pointer-events-none">
        <img
          src="/web_images/Andlegur%20web%2030.webp"
          alt="Users from different backgrounds connecting and growing together in a supportive digital sanctuary, surrounded by calming celestial elements and nature-inspired motifs."
          className="w-full h-full object-cover opacity-20 mix-blend-screen"
          loading="eager"
          aria-hidden="true"
        />
        {/* Radial vignette — image visible at edges, darker in centre for text readability */}
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 60% 65% at 50% 50%, #05091Bbb 0%, #05091B11 65%, #05091B 100%)' }}
        />
      </div>
    </section>
  );
};

export default Heroabout;
